import { ChangeDetectionStrategy, Component, OnInit, signal, ViewChild } from "@angular/core";
import { S25ItemI } from "../../pojo/S25ItemI";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25States } from "../../services/state/state.map";
import { S25PanelsModule } from "../s25-panel/s25.panels.module";
import { StudentEngagementService } from "./student.engagement.service";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { DataIngestionModule } from "../data-ingestion/data.ingestion.module";
import { SeOrganizationTileComponent } from "./organization/se.organization.tile.component";
import { SlicePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { SeEventTileComponent } from "./organization/se.event.tile.component";
import { StudentEngagementOrg } from "./StudentEngagementOrg";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { LoginService } from "../../services/login.service";
import SeOrg = StudentEngagementOrg.SeOrg;
import { SeSearchComponent } from "./search/se.search.component";
import { SeSearchResultsComponent } from "./search/tile.search.results.component";
import { SeEvent } from "./event/se.event.details.view.component";

@TypeManagerDecorator("s25-ng-se-home")
@Component({
    selector: "s25-ng-se-home",
    template: `
        @if (init()) {
            <section
                class="hero-search-section"
                [style.background-image]="heroBackgroundImage ? 'url(' + heroBackgroundImage + ')' : ''"
            >
                <div class="hero-content">
                    <h1 class="hero-title">Student Engagement</h1>
                    <p class="hero-subtitle">Connect, collaborate, and make an impact on campus</p>
                    <div class="search-container">
                        <s25-ng-se-search #searchComponent></s25-ng-se-search>
                    </div>
                </div>
            </section>

            <!-- Search Results Section - Shows first when user has searched -->
            @if (searchComponent.hasSearched()) {
                <section class="c-margin-top--single c-margin-bottom--single">
                    <h2>Search Results</h2>
                    <s25-ng-se-search-results
                        [results]="searchComponent.results()"
                        [resultsCount]="searchComponent.resultsCount()"
                        [hasSearched]="searchComponent.hasSearched()"
                        [isSearching]="searchComponent.isSearching()"
                        [searchType]="searchComponent.searchType()"
                        [currentPage]="searchComponent.currentPage()"
                        [pageSize]="searchComponent.pageSize()"
                        (loadMore)="searchComponent.onLoadMore()"
                        (pageChange)="searchComponent.onPageChange($event)"
                    ></s25-ng-se-search-results>
                </section>
            }

            @if (tasks().length) {
                <section class="c-margin-top--single c-margin-bottom--single">
                    <h2>My Tasks</h2>
                    <div class="tasks-container">
                        @for (task of tasks(); track task.itemId) {
                            <a class="task-link">
                                <s25-ng-icon [type]="'check'" [size]="20"></s25-ng-icon>
                                <span>{{ task.itemName }}</span>
                            </a>
                        }
                    </div>
                </section>
            }
            @if (organizations().length) {
                <section class="c-margin-top--single c-margin-bottom--single">
                    <h2>{{ (isLoggedIn() ? "My " : "") + "Organizations" }}</h2>
                    <div class="organizations-grid">
                        @for (org of organizations() | slice: 0 : numVisibleOrgs(); track org.itemId) {
                            <se-organization-tile
                                [org]="org"
                                [routingStateName]="stateMap.studentEngagementOrganization"
                            ></se-organization-tile>
                        }
                    </div>
                    @if (organizations().length > numVisibleOrgs()) {
                        <button class="c-textButton" (click)="numVisibleOrgs.set(numVisibleOrgs() + 6)">
                            See More Organizations
                        </button>
                    }
                </section>
            }
            <section class="c-margin-top--single c-margin-bottom--single">
                <h2>Upcoming Events</h2>
                <div class="events-grid">
                    @for (event of upcomingEvents() | slice: 0 : numVisibleEvents(); track event.itemId) {
                        <se-event-tile
                            [event]="event"
                            [routingStateName]="stateMap.studentEngagementEvent"
                        ></se-event-tile>
                    }
                </div>
                @if (upcomingEvents().length > numVisibleEvents()) {
                    <button class="c-textButton" (click)="numVisibleEvents.set(numVisibleEvents() + 6)">
                        See More Events
                    </button>
                }
            </section>
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
    `,
    styles: `
        .hero-search-section {
            position: relative;
            min-height: 300px;
            background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
            background-size: cover;
            background-position: center;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 2rem;
            border-radius: 4px;
        }

        .hero-content {
            position: relative;
            z-index: 2;
            text-align: center;
            padding: 2rem;
            max-width: 800px;
            width: 100%;
        }

        .hero-title {
            font-size: 2.5rem;
            color: #fff;
            margin: 0 0 0.5rem 0;
            font-weight: 700;
        }

        .hero-subtitle {
            font-size: 1.125rem;
            color: rgba(255, 255, 255, 0.9);
            margin: 0 0 2rem 0;
        }

        h2 {
            font-size: 24px;
            margin-bottom: 20px;
            color: #333;
            border-bottom: 2px solid #f0f0f0;
            padding-bottom: 0.5em;
        }

        .tasks-container {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
        }

        .task-link {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background-color: #f3f4f6;
            color: #374151;
            text-decoration: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
            box-shadow: 0 2px 4px rgba(0, 102, 204, 0.1);
        }

        .task-link:hover {
            background-color: #e5e7eb;
            color: #111827;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 102, 204, 0.2);
        }

        .task-link:active {
            transform: translateY(0);
            box-shadow: 0 2px 4px rgba(0, 102, 204, 0.1);
        }

        .task-link span {
            line-height: 1;
        }

        ::ng-deep .task-link svg {
            vertical-align: middle;
        }

        .organizations-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
            gap: 20px;
        }

        .events-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1.5rem;
        }

        .c-textButton {
            margin: 1em auto;
            display: block;
        }

        s25-ng-loading-inline-static {
            display: block;
            margin: 0 auto;
            width: fit-content;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25PanelsModule,
        S25LoadingInlineModule,
        DataIngestionModule,
        SeOrganizationTileComponent,
        SlicePipe,
        SeEventTileComponent,
        S25IconComponent,
        FormsModule,
        SeSearchComponent,
        SeSearchResultsComponent,
    ],
})
export class SeHomeComponent implements OnInit {
    init = signal(false);

    isLoggedIn = signal(false);
    organizations = signal<SeOrg[]>([]);
    upcomingEvents = signal<SeEvent[]>([]);
    tasks = signal<S25ItemI[]>([]);
    numVisibleOrgs = signal(6);
    numVisibleEvents = signal(6);

    stateMap = S25States.names;
    heroBackgroundImage: string | null = null; // Set this to a URL to use a custom or static image as background for search area, remove if not needed

    async ngOnInit(): Promise<void> {
        this.isLoggedIn.set(await LoginService.getLoggedIn());
        // TODO: if allowing custom banner/hero image for search area, fetch that here
        await Promise.all([this.getOrganizations(), this.getUpcomingEvents(), this.getTasks()]);
        this.init.set(true);
    }

    async getOrganizations() {
        const promise = this.isLoggedIn() ? StudentEngagementService.getMyOrgs : StudentEngagementService.getAllOrgs;
        const orgs = await promise();
        this.organizations.set(orgs);
    }

    async getUpcomingEvents() {
        const promise = this.isLoggedIn()
            ? StudentEngagementService.getMyUpcomingEvents
            : StudentEngagementService.getAllUpcomingEvents;
        const events = await promise();
        this.upcomingEvents.set(events);
    }

    async getTasks() {
        if (this.isLoggedIn()) {
            const tasks = await StudentEngagementService.getTasks();
            this.tasks.set(tasks);
        }
    }

    handleSearch() {
        // TODO: Route to search view or render results in current view
    }
}
